// Главный экран — дашборд «Сегодня». Porт из
// designproject/files/screens-feed.jsx (HomeC). Данные реальные (hh-z84):
//   GET /api/v1/properties/tree   — счётчик инвентаря
//   GET /api/v1/items/orphans     — счётчик «без места»
//   GET /api/v1/tasks?from=&to=   — ближайшие задачи ТО («Что важно сейчас»)
//   GET /api/v1/messages          — последняя активность
// Заглушечный mockData.ts удалён.
//
// hh-rv2: дашборд не статичен. Команда в voice-dock / на экране чата меняет
// инвентарь и ленту, поэтому экран подписан на общий useChatHistory — каждый
// завершённый проход (activeRun → 'done') триггерит фоновую перезагрузку, и
// «Последняя активность» здесь и на экране Ассистента больше не расходятся.

import type { JSX } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { T, Icon, type IconName } from '../../tokens';
import type { RouterApi } from '../../router';
import {
  properties as propertiesApi,
  items as itemsApi,
  tasks as tasksApi,
  messages as messagesApi,
  humanizeError,
} from '../../api';
import type { UseChatHistory } from '../chat';
import { TodayCard } from './TodayCard';
import { LastActivity } from './LastActivity';
import type {
  DashboardCounters,
  FeedEntryAction,
  LastActivityEntry,
  TaskTone,
  TodayTask,
} from './types';

interface FeedScreenProps {
  readonly router: RouterApi;
  /**
   * Общий на всё приложение чат (useChatHistory из AppShell). Дашборд читает
   * `activeRun`, чтобы перезагрузиться, когда команда отработала (hh-rv2).
   */
  readonly chat: UseChatHistory;
}

interface FeedData {
  readonly counters: DashboardCounters;
  readonly tasks: readonly TodayTask[];
  readonly activity: readonly LastActivityEntry[];
}

type Status =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly data: FeedData };

const DAY_MS = 24 * 60 * 60 * 1000;

export function FeedScreen({ router, chat }: FeedScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  // Жив ли компонент + монотонный токен загрузки: фоновые перезагрузки
  // вытесняют друг друга, устаревший ответ не перетирает свежий.
  const aliveRef = useRef<boolean>(true);
  const loadSeq = useRef<number>(0);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  /**
   * Загрузка дашборда. `silent` — фоновое обновление после команды: не
   * показываем спиннер на месте уже отрисованных данных и при сбое оставляем
   * прежнее содержимое, а не экран ошибки.
   */
  const load = useCallback((silent: boolean): void => {
    const seq = (loadSeq.current += 1);
    if (!silent) setStatus({ kind: 'loading' });

    const now = Date.now();
    const from = new Date(now - 30 * DAY_MS).toISOString();
    const to = new Date(now + 30 * DAY_MS).toISOString();

    void Promise.all([
      propertiesApi.getPropertyTree(),
      itemsApi.listOrphans(),
      tasksApi.listTasks(from, to),
      messagesApi.listMessages(12),
    ])
      .then(([tree, orphans, taskRows, msgs]) => {
        if (!aliveRef.current || seq !== loadSeq.current) return;
        setStatus({ kind: 'ready', data: buildFeedData(tree, orphans, taskRows, msgs) });
      })
      .catch((err: unknown) => {
        if (!aliveRef.current || seq !== loadSeq.current || silent) return;
        setStatus({ kind: 'error', message: describeError(err) });
      });
  }, []);

  // Первичная загрузка.
  useEffect(() => {
    load(false);
  }, [load]);

  // hh-rv2: каждый завершённый проход команды перезагружает дашборд. Ref на
  // последний обработанный runId отсекает повторы (activeRun держит 'done' до
  // следующего прохода) и сброс activeRun → null при старте нового.
  const handledRunRef = useRef<number | null>(null);
  const run = chat.activeRun;
  useEffect(() => {
    if (run === null || run.stage !== 'done') return;
    if (handledRunRef.current === run.runId) return;
    handledRunRef.current = run.runId;
    load(true);
  }, [run, load]);

  const openTask = (task: TodayTask): void => {
    if (task.equipmentId !== undefined) {
      router.go('eq_card', { id: task.equipmentId });
      return;
    }
    if (task.isSeasonal === true) {
      router.go('season');
    }
  };

  return (
    <div style={containerStyle}>
      <Header
        title="aidepo"
        subtitle="всё, что у тебя есть, помнится"
        onOpenNotifs={() => router.go('notifs')}
        onOpenProfile={() => router.go('profile')}
      />

      <div style={scrollStyle}>
        {status.kind === 'loading' && <CenteredNote text="Загружаю…" />}
        {status.kind === 'error' && (
          <CenteredNote text={`Не удалось загрузить: ${status.message}`} tone="danger" />
        )}
        {status.kind === 'ready' && (
          <Dashboard data={status.data} router={router} onOpenTask={openTask} />
        )}
      </div>
    </div>
  );
}

interface DashboardProps {
  readonly data: FeedData;
  readonly router: RouterApi;
  readonly onOpenTask: (task: TodayTask) => void;
}

function Dashboard({ data, router, onOpenTask }: DashboardProps): JSX.Element {
  const { counters, tasks, activity } = data;
  return (
    <>
      <div style={statsGridStyle}>
        <BigStat
          label="В учёте"
          value={String(counters.inventoryCount)}
          sub="вещей и оборудования"
          onClick={() => router.go('tree')}
        />
        <BigStat
          label="ТО на неделе"
          value={String(counters.weekTasks)}
          sub={
            counters.weekTasks === 0
              ? 'спокойная неделя'
              : `${counters.weekSafety} safety · ${counters.weekTasks - counters.weekSafety} обычное`
          }
          {...(counters.weekSafety > 0 ? { tone: 'safety' as const } : {})}
          onClick={() => router.go('calendar', { mode: 'week' })}
        />
        <BigStat
          label="Без места"
          value={String(counters.orphanCount)}
          sub={counters.orphanCount === 0 ? 'всё разложено' : 'нужно разложить'}
          {...(counters.orphanCount > 0 ? { tone: 'orphan' as const } : {})}
          onClick={() => router.go('tree')}
        />
      </div>

      <LastActivity
        entries={activity}
        onOpenHistory={() => router.go('changes')}
      />

      <div style={sectionLabelStyle}>Что важно сейчас</div>
      {tasks.length === 0 ? (
        <div style={emptyTasksStyle}>Ближайших задач по обслуживанию нет.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tasks.map((task) => (
            <TodayCard key={task.id} task={task} onClick={() => onOpenTask(task)} />
          ))}
        </div>
      )}
    </>
  );
}

// ─── Сборка данных дашборда из ответов API ──────────────────────────────────

function buildFeedData(
  tree: { readonly properties: readonly PropertyTreeLike[] },
  orphans: { readonly count: number },
  taskRows: readonly TaskRowLike[],
  msgs: { readonly messages: readonly MessageRowLike[] },
): FeedData {
  let inventoryCount = 0;
  for (const p of tree.properties) {
    inventoryCount += p.items.length + p.equipment.length;
    for (const r of p.rooms) inventoryCount += r.items.length + r.equipment.length;
  }

  const weekHorizon = Date.now() + 7 * DAY_MS;
  const pending = taskRows
    .filter((t) => !t.isDone)
    .slice()
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

  const weekRows = pending.filter((t) => Date.parse(t.date) <= weekHorizon);
  const counters: DashboardCounters = {
    inventoryCount,
    weekTasks: weekRows.length,
    weekSafety: weekRows.filter((t) => t.isSafety).length,
    orphanCount: orphans.count,
  };

  const tasks: readonly TodayTask[] = pending.slice(0, 5).map(taskToCard);

  // Последние 3 сообщения, новейшее первым (List отдаёт от старых к новым).
  const activity: readonly LastActivityEntry[] = msgs.messages
    .slice(-3)
    .reverse()
    .map(messageToEntry);

  return { counters, tasks, activity };
}

function taskToCard(t: TaskRowLike): TodayTask {
  const tone: TaskTone = t.isSafety ? 'safety' : t.isSeasonal ? 'brand' : 'neutral';
  const icon: IconName = t.isSafety ? 'shield' : t.isSeasonal ? 'leaf' : 'cal';
  return {
    id: t.id,
    tone,
    icon,
    when: relativeWhen(t.date),
    title: t.title,
    sub: t.isSeasonal ? 'Сезонный чек-лист' : 'Задача по обслуживанию',
    ...(t.equipmentId !== null ? { equipmentId: t.equipmentId } : {}),
    ...(t.isSeasonal ? { isSeasonal: true } : {}),
  };
}

function messageToEntry(m: MessageRowLike): LastActivityEntry {
  const actions: readonly FeedEntryAction[] = (m.actions ?? []).map((a) => ({
    name: a.name,
    ...(a.to !== null && a.to !== undefined ? { to: a.to } : {}),
    ...(a.from !== null && a.from !== undefined ? { from: a.from } : {}),
  }));
  const kind: LastActivityEntry['kind'] =
    m.role === 'user' ? 'user' : actions.length > 0 ? 'ai_summary' : 'ai_text';
  return {
    id: m.id,
    kind,
    time: activityTime(m.createdAt),
    text: m.content,
    ...(actions.length > 0 ? { actions } : {}),
  };
}

/** «просрочено» / «сегодня» / «завтра» / «через N дн.» от ISO-даты. */
function relativeWhen(iso: string): string {
  const days = Math.round((Date.parse(iso) - Date.now()) / DAY_MS);
  if (Number.isNaN(days)) return '';
  if (days < 0) return 'просрочено';
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  return `через ${days} дн.`;
}

/** Короткая метка времени активности: «сегодня, HH:MM» / «вчера» / «DD.MM». */
function activityTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return `сегодня, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  const yest = new Date(today.getTime() - DAY_MS);
  if (d.toDateString() === yest.toDateString()) return 'вчера';
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

// Минимальные структурные типы ответов API — берём только нужные поля.
interface NodeItemsLike {
  readonly items: readonly unknown[];
  readonly equipment: readonly unknown[];
}
interface PropertyTreeLike extends NodeItemsLike {
  readonly rooms: readonly NodeItemsLike[];
}
interface TaskRowLike {
  readonly id: string;
  readonly date: string;
  readonly title: string;
  readonly equipmentId: string | null;
  readonly isSafety: boolean;
  readonly isSeasonal: boolean;
  readonly isDone: boolean;
}
interface MessageRowLike {
  readonly id: string;
  readonly role: string;
  readonly content: string;
  readonly createdAt: string;
  readonly actions?: readonly {
    readonly name: string;
    readonly to: string | null;
    readonly from: string | null;
  }[];
}

// ─── CenteredNote ────────────────────────────────────────────────────────────

function CenteredNote({
  text,
  tone,
}: {
  readonly text: string;
  readonly tone?: 'danger';
}): JSX.Element {
  return (
    <div
      style={{
        padding: '40px 20px',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 500,
        color: tone === 'danger' ? T.danger : T.ink3,
      }}
    >
      {text}
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

interface HeaderProps {
  readonly title: string;
  readonly subtitle: string;
  readonly onOpenNotifs: () => void;
  readonly onOpenProfile: () => void;
}

function Header({
  title,
  subtitle,
  onOpenNotifs,
  onOpenProfile,
}: HeaderProps): JSX.Element {
  return (
    <div
      style={{
        padding: '10px 20px 16px',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 8,
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: -0.6,
            color: T.ink,
            lineHeight: 1.1,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: T.ink3,
            marginTop: 4,
            fontWeight: 500,
          }}
        >
          {subtitle}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenNotifs}
        aria-label="Уведомления"
        style={{
          width: 38,
          height: 38,
          borderRadius: 19,
          background: T.surface,
          border: `1px solid ${T.border}`,
          cursor: 'pointer',
          color: T.ink2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="bell" size={18} />
      </button>
      <button
        type="button"
        onClick={onOpenProfile}
        aria-label="Профиль"
        style={{
          width: 38,
          height: 38,
          borderRadius: 19,
          background: T.ink,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontFamily: T.font,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="user" size={18} color="#fff" />
      </button>
    </div>
  );
}

// ─── BigStat ───────────────────────────────────────────────────────────────

type BigStatTone = 'safety' | 'orphan';

interface BigStatPalette {
  readonly fg: string;
  readonly bg: string;
  readonly bd: string;
}

const BIG_STAT_TONES: Record<BigStatTone, BigStatPalette> = {
  safety: { fg: T.safety, bg: T.safetySoft, bd: '#E8D7A8' },
  orphan: { fg: T.safety, bg: T.safetySoft, bd: '#E8D7A8' },
};

const BIG_STAT_NEUTRAL: BigStatPalette = {
  fg: T.ink,
  bg: T.surface,
  bd: T.border,
};

interface BigStatProps {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone?: BigStatTone;
  readonly onClick: () => void;
}

function BigStat({ label, value, sub, tone, onClick }: BigStatProps): JSX.Element {
  const palette = tone !== undefined ? BIG_STAT_TONES[tone] : BIG_STAT_NEUTRAL;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.bd}`,
        borderRadius: 14,
        padding: 12,
        textAlign: 'left',
        fontFamily: T.font,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: T.ink3,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          lineHeight: 1.1,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: palette.fg,
          letterSpacing: -0.5,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: T.ink3,
          lineHeight: 1.25,
          marginTop: 2,
        }}
      >
        {sub}
      </div>
    </button>
  );
}

// ─── Layout styles ─────────────────────────────────────────────────────────

const containerStyle: JSX.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
};

const scrollStyle: JSX.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '0 16px 24px',
};

const statsGridStyle: JSX.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 8,
  marginBottom: 16,
};

const sectionLabelStyle: JSX.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: T.ink3,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  padding: '24px 4px 10px',
};

const emptyTasksStyle: JSX.CSSProperties = {
  padding: '18px 14px',
  background: T.surfaceAlt,
  border: `1px solid ${T.border}`,
  borderRadius: 14,
  fontSize: 13,
  fontWeight: 500,
  color: T.ink3,
  textAlign: 'center',
};
